import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchInquiries,
  fetchInquiryCounts,
  rejectInquiry,
  promoteInquiry,
  deleteInquiry,
  type InquiryListItem,
  type PromotePayload,
} from "@/api/inquiries";
import { fetchBookingConflicts } from "@/api/bookings";
import { fetchUnits } from "@/api/newBooking";
import { getSettingsConfig } from "@/api/settings";
import type { ApiError } from "@/api/client";
import { Btn } from "@/components/Btn";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { Modal } from "@/components/dialogs/Modal";
import { ConflictModal, type ConflictEntry } from "@/components/dialogs/ConflictModal";
import { useDateRange } from "@/lib/useDateRange";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { formatDatum } from "@/lib/format";

// Attribut-Filter wie die Tabs der Buchungsliste.
type Tab = "offen" | "importiert" | "abgelehnt";

const TAB_LABELS: Record<Tab, string> = {
  offen: "Offen",
  importiert: "Importiert",
  abgelehnt: "Abgelehnt",
};

const TAB_STATUS_FILTER: Record<Tab, string> = {
  offen: "new,verified",
  importiert: "imported",
  abgelehnt: "rejected",
};

const ITEMS_PER_PAGE = 10;

const thStyle: React.CSSProperties = {
  padding: "10px 14px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: "var(--fg-muted)",
  textTransform: "uppercase",
  letterSpacing: ".05em",
  whiteSpace: "nowrap",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  border: "1px solid var(--border)",
  borderRadius: 6,
  background: "var(--bg-card)",
  color: "var(--fg)",
  fontFamily: "var(--font-body)",
  boxSizing: "border-box",
};

const dialogLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--fg-muted)",
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: ".04em",
};

/** "120,50" → 120.5; leer → undefined; ungültig → NaN. */
function parseBetrag(s: string): number | undefined {
  const t = s.trim().replace(",", ".");
  if (t === "") return undefined;
  return Number(t);
}

// ---------------------------------------------------------------------------
// Ablehnen-Dialog
// ---------------------------------------------------------------------------

function RejectDialog({
  inquiry,
  onClose,
  onConfirm,
  loading,
}: {
  inquiry: InquiryListItem;
  onClose: () => void;
  onConfirm: (grund: string) => void;
  loading: boolean;
}) {
  const [grund, setGrund] = useState("");

  return (
    <Modal maxWidth={440}>
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 8px", color: "var(--fg)" }}>
        Anfrage ablehnen
      </h2>
      <p style={{ fontSize: 13, color: "var(--fg-muted)", margin: "0 0 16px" }}>
        Anfrage <strong>#{inquiry.id}</strong> von <strong>{inquiry.gruppenname}</strong> ablehnen?
      </p>
      <label style={dialogLabelStyle}>Ablehnungsgrund (optional)</label>
      <textarea
        value={grund}
        onChange={(e) => setGrund(e.target.value)}
        rows={3}
        placeholder="z. B. Kein freier Termin"
        style={{ ...inputStyle, resize: "vertical" }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>
          Abbrechen
        </Btn>
        <Btn variant="danger" onClick={() => onConfirm(grund)} disabled={loading}>
          {loading ? "Wird abgelehnt…" : "Ablehnen"}
        </Btn>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Löschen-Dialog
// ---------------------------------------------------------------------------

function DeleteDialog({
  inquiry,
  onClose,
  onConfirm,
  loading,
}: {
  inquiry: InquiryListItem;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <Modal maxWidth={440}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span className="material-icons-outlined" style={{ color: "#ef4444", fontSize: 24 }}>
          delete_forever
        </span>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--fg)" }}>
          Anfrage löschen
        </h2>
      </div>
      <p style={{ fontSize: 13, color: "var(--fg-muted)", margin: "0 0 16px" }}>
        Anfrage <strong>#{inquiry.id}</strong> von <strong>{inquiry.gruppenname || inquiry.kontakt}</strong>{" "}
        endgültig löschen? Das kann nicht rückgängig gemacht werden.
        {inquiry.imported_booking_id != null && (
          <> Die daraus entstandene Buchung <strong>#{inquiry.imported_booking_id}</strong> bleibt erhalten.</>
        )}
      </p>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Btn variant="ghost" onClick={onClose}>
          Abbrechen
        </Btn>
        <Btn variant="danger" onClick={onConfirm} disabled={loading}>
          {loading ? "Wird gelöscht…" : "Endgültig löschen"}
        </Btn>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Übernehmen-Dialog (Einheiten aus der Anfrage, nur Anzahlung wählbar,
// Live-Konfliktprüfung; Status ist nach Übernahme immer "Angelegt")
// ---------------------------------------------------------------------------

export type PromoteFormValues = {
  einheiten_ids: number[];
  anzahlung_betrag?: number;
};

function PromoteDialog({
  inquiry,
  onClose,
  onConfirm,
  loading,
}: {
  inquiry: InquiryListItem;
  onClose: () => void;
  onConfirm: (values: PromoteFormValues) => void;
  loading: boolean;
}) {
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: fetchUnits });
  const { data: settingsConfig } = useQuery({
    queryKey: ["settings-config"],
    queryFn: getSettingsConfig,
    retry: false,
  });

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [anzahlung, setAnzahlung] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Einheiten kommen aus dem Einheitenwunsch der Anfrage. Nur wenn sich daraus
  // keine Einheit auflösen lässt, muss manuell gewählt werden.
  const wunschIds =
    units.length === 0
      ? []
      : (() => {
          const wunschNamen = (inquiry.einheiten_wunsch || "").split(",").map((s) => s.trim()).filter(Boolean);
          return units.filter((u) => wunschNamen.includes(u.name)).map((u) => u.id);
        })();
  const manuelleAuswahl = units.length > 0 && wunschIds.length === 0;

  useEffect(() => {
    if (units.length === 0) return;
    const wunschNamen = (inquiry.einheiten_wunsch || "").split(",").map((s) => s.trim()).filter(Boolean);
    const ids = units.filter((u) => wunschNamen.includes(u.name)).map((u) => u.id);
    if (ids.length > 0) setSelectedIds(ids);
  }, [units, inquiry.einheiten_wunsch]);

  // Anzahlung mit dem Config-Default vorbelegen, solange nichts eingetragen ist.
  useEffect(() => {
    if (settingsConfig?.ANZAHLUNG_BETRAG != null) {
      setAnzahlung((prev) => (prev === "" ? settingsConfig.ANZAHLUNG_BETRAG : prev));
    }
  }, [settingsConfig]);

  // Live-Konfliktprüfung für den Anfrage-Zeitraum + gewählte Einheiten.
  const { data: conflictData, isFetching: conflictsLoading } = useQuery({
    queryKey: ["inquiry-conflicts", inquiry.id, selectedIds],
    queryFn: () =>
      fetchBookingConflicts({
        einheiten_ids: selectedIds,
        check_in: inquiry.check_in,
        check_out: inquiry.check_out,
        check_in_time: inquiry.check_in_time || undefined,
        check_out_time: inquiry.check_out_time || undefined,
      }),
    enabled: selectedIds.length > 0,
    retry: false,
  });
  const conflicts = conflictData?.conflicts ?? [];

  function toggleUnit(id: number) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function submit() {
    const az = parseBetrag(anzahlung);
    if (az !== undefined && (Number.isNaN(az) || az < 0)) {
      setFieldError("Anzahlung muss eine Zahl ≥ 0 sein.");
      return;
    }
    setFieldError(null);
    onConfirm({
      einheiten_ids: selectedIds,
      anzahlung_betrag: az,
    });
  }

  const zeitraum = `${formatDatum(inquiry.check_in)} – ${formatDatum(inquiry.check_out)}`;
  const zeiten =
    inquiry.check_in_time || inquiry.check_out_time
      ? ` (${inquiry.check_in_time || "15:00"} / ${inquiry.check_out_time || "10:00"})`
      : "";

  return (
    <Modal maxWidth={560} contentStyle={{ maxHeight: "90vh", overflowY: "auto" }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 8px", color: "var(--fg)" }}>
        Anfrage übernehmen
      </h2>
      <p style={{ fontSize: 13, color: "var(--fg-muted)", margin: "0 0 12px" }}>
        Anfrage <strong>#{inquiry.id}</strong> von <strong>{inquiry.gruppenname}</strong> als Buchung anlegen.
      </p>

      {/* Kontext-Infos */}
      <div
        style={{
          background: "var(--bg-subtle)",
          border: "1px solid var(--border)",
          borderRadius: 6,
          padding: "10px 12px",
          marginBottom: 12,
          fontSize: 12,
          color: "var(--fg-muted)",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "4px 12px",
        }}
      >
        <div>
          <span style={{ fontWeight: 600 }}>Zeitraum:</span> {zeitraum}
          {zeiten}
        </div>
        {inquiry.gruppenart && (
          <div>
            <span style={{ fontWeight: 600 }}>Gruppenart:</span> {inquiry.gruppenart}
          </div>
        )}
        <div>
          <span style={{ fontWeight: 600 }}>Kontakt:</span> {inquiry.kontakt}
          {inquiry.telefon ? ` · ${inquiry.telefon}` : ""}
        </div>
        {inquiry.einheiten_wunsch && (
          <div>
            <span style={{ fontWeight: 600 }}>Einheitenwunsch:</span> {inquiry.einheiten_wunsch}
          </div>
        )}
        {inquiry.adresse_ort && (
          <div style={{ gridColumn: "1/-1" }}>
            <span style={{ fontWeight: 600 }}>Adresse:</span>{" "}
            {[inquiry.adresse_strasse, `${inquiry.adresse_plz} ${inquiry.adresse_ort}`.trim(), inquiry.adresse_land]
              .filter(Boolean)
              .join(", ")}
          </div>
        )}
      </div>

      {/* Nachricht des Gasts — Entscheidungsgrundlage, deshalb prominent. */}
      {inquiry.nachricht && (
        <div style={{ marginBottom: 16 }}>
          <div style={dialogLabelStyle}>Nachricht des Gasts</div>
          <div
            style={{
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "10px 12px",
              fontSize: 13,
              color: "var(--fg)",
              whiteSpace: "pre-wrap",
              maxHeight: 160,
              overflowY: "auto",
              background: "var(--bg-card)",
            }}
          >
            {inquiry.nachricht}
          </div>
        </div>
      )}

      {/* Einheiten: kommen aus der Anfrage; manuelle Auswahl nur als Fallback,
          wenn der Einheitenwunsch keiner Einheit zugeordnet werden kann. */}
      <div style={dialogLabelStyle}>
        {manuelleAuswahl ? (
          <>
            Einheiten <span style={{ color: "var(--accent)" }}>*</span>
          </>
        ) : (
          "Einheiten (aus der Anfrage)"
        )}
      </div>
      {manuelleAuswahl && (
        <p style={{ fontSize: 12, color: "var(--status-cancel)", margin: "0 0 8px" }}>
          Der Einheitenwunsch der Anfrage konnte keiner Einheit zugeordnet werden — bitte manuell wählen.
        </p>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        {(manuelleAuswahl ? units : units.filter((u) => selectedIds.includes(u.id))).map((u) => {
          const selected = selectedIds.includes(u.id);
          return (
            <button
              key={u.id}
              type="button"
              onClick={manuelleAuswahl ? () => toggleUnit(u.id) : undefined}
              disabled={!manuelleAuswahl}
              style={{
                padding: "6px 14px",
                borderRadius: 20,
                border: `2px solid ${selected ? "var(--brand-700)" : "var(--border)"}`,
                background: selected ? "var(--brand-700)" : "var(--bg-card)",
                color: selected ? "#fff" : "var(--fg)",
                fontSize: 13,
                fontWeight: 600,
                cursor: manuelleAuswahl ? "pointer" : "default",
                fontFamily: "var(--font-body)",
              }}
            >
              {u.name}
            </button>
          );
        })}
      </div>

      {/* Live-Konflikthinweis */}
      {selectedIds.length > 0 && conflicts.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            background: "color-mix(in oklab, var(--status-cancel) 10%, var(--bg-card))",
            border: "1px solid var(--status-cancel)",
            borderRadius: 6,
            padding: "10px 12px",
            marginBottom: 16,
            fontSize: 12,
            color: "var(--fg)",
          }}
        >
          <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--status-cancel)" }}>
            warning
          </span>
          <div>
            <strong>
              {conflicts.length} Belegungskonflikt{conflicts.length !== 1 ? "e" : ""} im Zeitraum:
            </strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
              {conflicts.slice(0, 4).map((c) => (
                <li key={c.id}>
                  {c.gastname} · {c.einheiten_namen} · {formatDatum(c.check_in)} – {formatDatum(c.check_out)}
                </li>
              ))}
              {conflicts.length > 4 && <li>… und {conflicts.length - 4} weitere</li>}
            </ul>
          </div>
        </div>
      )}
      {selectedIds.length > 0 && !conflictsLoading && conflicts.length === 0 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            fontSize: 12,
            color: "var(--status-closed)",
            marginBottom: 16,
          }}
        >
          <span className="material-icons-outlined" style={{ fontSize: 16 }}>
            check_circle
          </span>
          Zeitraum ist für die gewählten Einheiten frei.
        </div>
      )}

      {/* Anzahlung — einziges wählbares Feld; die Buchung startet immer im Status "Angelegt". */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginBottom: 4 }}>
        <div>
          <label style={dialogLabelStyle}>Anzahlung (€)</label>
          <input
            value={anzahlung}
            onChange={(e) => setAnzahlung(e.target.value)}
            placeholder={settingsConfig?.ANZAHLUNG_BETRAG ?? "100"}
            style={inputStyle}
          />
        </div>
      </div>

      {fieldError && (
        <div style={{ fontSize: 12, color: "var(--status-cancel)", marginTop: 8 }}>{fieldError}</div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>
          Abbrechen
        </Btn>
        <Btn variant="primary" onClick={submit} disabled={selectedIds.length === 0 || loading}>
          {loading ? "Wird übernommen…" : "Als Buchung übernehmen"}
        </Btn>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Haupt-Komponente
// ---------------------------------------------------------------------------

export function InquiriesPage() {
  useDocumentTitle("Anfragen");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("offen");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [rejectTarget, setRejectTarget] = useState<InquiryListItem | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<InquiryListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InquiryListItem | null>(null);
  const [conflicts, setConflicts] = useState<ConflictEntry[] | null>(null);
  const [pendingPromote, setPendingPromote] = useState<{ id: number; payload: PromotePayload } | null>(null);

  // Zeitraum-Filter (serverseitig, gleiche Semantik wie Buchungsliste)
  const [filterVon, setFilterVon] = useState("");
  const [filterBis, setFilterBis] = useState("");
  const filterDateRange = useDateRange(
    filterVon,
    filterBis,
    ({ from, to }) => {
      setFilterVon(from);
      setFilterBis(to);
      setPage(1);
    },
    { minNights: 0, keepDuration: false },
  );

  const { data: inquiries = [], isLoading, isError } = useQuery({
    queryKey: ["inquiries", tab, filterVon, filterBis],
    queryFn: () =>
      fetchInquiries({
        status_filter: TAB_STATUS_FILTER[tab],
        von: filterVon || undefined,
        bis: filterBis || undefined,
      }),
    retry: false,
  });

  const { data: counts } = useQuery({
    queryKey: ["inquiries", "counts"],
    queryFn: fetchInquiryCounts,
    retry: false,
  });

  const getCount = (t: Tab): number => {
    if (!counts) return 0;
    return t === "offen" ? counts.offen : t === "importiert" ? counts.importiert : counts.abgelehnt;
  };

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["inquiries"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const rejectMut = useMutation({
    mutationFn: ({ id, grund }: { id: number; grund: string }) => rejectInquiry(id, grund),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      showToast("Anfrage wurde abgelehnt.");
    },
    onError: (err: Error) => {
      showToast(err.message || "Fehler beim Ablehnen.", false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteInquiry(id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      showToast("Anfrage wurde gelöscht.");
    },
    onError: (err: Error) => {
      showToast(err.message || "Fehler beim Löschen.", false);
    },
  });

  const promoteMut = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: PromotePayload }) => promoteInquiry(id, payload),
    onSuccess: (bk) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["calendar"] });
      setPromoteTarget(null);
      setConflicts(null);
      setPendingPromote(null);
      showToast(`Buchung #${bk.id} angelegt.`);
      navigate(`/bookings/${bk.id}`);
    },
    onError: (err: unknown) => {
      const apiErr = err as ApiError;
      if (apiErr?.status === 409) {
        const body = apiErr.body as { detail?: { conflicts?: ConflictEntry[] } };
        const cs = body?.detail?.conflicts;
        if (cs && cs.length > 0) {
          setConflicts(cs);
          return;
        }
      }
      showToast((err as Error).message || "Fehler beim Übernehmen.", false);
    },
  });

  function startPromote(values: PromoteFormValues) {
    if (!promoteTarget) return;
    const payload: PromotePayload = {
      einheiten_ids: values.einheiten_ids,
      anzahlung_betrag: values.anzahlung_betrag,
      force: false,
      bemerkung_prefix: "Anfrage von Website",
    };
    setPendingPromote({ id: promoteTarget.id, payload });
    promoteMut.mutate({ id: promoteTarget.id, payload });
  }

  function forcePromote() {
    if (!pendingPromote) return;
    setConflicts(null);
    promoteMut.mutate({
      id: pendingPromote.id,
      payload: { ...pendingPromote.payload, force: true },
    });
  }

  // Clientseitige Suche über Gruppe/Kontakt/E-Mail/Ort (wie Buchungsliste).
  const filtered = inquiries.filter((inq) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (inq.gruppenname || "").toLowerCase().includes(q) ||
      (inq.kontakt || "").toLowerCase().includes(q) ||
      (inq.email || "").toLowerCase().includes(q) ||
      (inq.adresse_ort || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const hasActiveFilter = filterVon !== "" || filterBis !== "";

  return (
    <div style={{ padding: "24px 32px" }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            background: toast.ok ? "var(--brand-700)" : "#b91c1c",
            color: "#fff",
            padding: "10px 18px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            zIndex: 300,
            boxShadow: "0 4px 16px rgba(0,0,0,.18)",
          }}
        >
          {toast.msg}
        </div>
      )}

      {/* Dialogs */}
      {rejectTarget && (
        <RejectDialog
          inquiry={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onConfirm={(grund) => rejectMut.mutate({ id: rejectTarget.id, grund })}
          loading={rejectMut.isPending}
        />
      )}
      {deleteTarget && (
        <DeleteDialog
          inquiry={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteMut.mutate(deleteTarget.id)}
          loading={deleteMut.isPending}
        />
      )}
      {promoteTarget && !conflicts && (
        <PromoteDialog
          inquiry={promoteTarget}
          onClose={() => setPromoteTarget(null)}
          onConfirm={startPromote}
          loading={promoteMut.isPending}
        />
      )}
      {conflicts && (
        <ConflictModal
          conflicts={conflicts}
          headline={
            <>
              Im gewählten Zeitraum gibt es bereits{" "}
              <strong>
                {conflicts.length} kollidierende Buchung{conflicts.length !== 1 ? "en" : ""}
              </strong>
              :
            </>
          }
          cancelLabel="Zurück"
          confirmLabel="Trotzdem übernehmen"
          onForce={forcePromote}
          onCancel={() => setConflicts(null)}
        />
      )}

      <PageHeader title="Buchungsanfragen" subtitle="Anfragen aus dem öffentlichen Formular prüfen und übernehmen" />

      {/* Tabs + Suche + Filter */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            background: "var(--bg-subtle)",
            borderRadius: 10,
            padding: 4,
            marginBottom: 16,
            width: "fit-content",
          }}
        >
          {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setPage(1);
                setExpandedId(null);
              }}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                fontWeight: 600,
                background: tab === t ? "var(--bg-card)" : "transparent",
                color: tab === t ? "var(--fg)" : "var(--fg-muted)",
                boxShadow: tab === t ? "0 1px 3px rgba(0,0,0,.08)" : "none",
                transition: "all .12s",
                whiteSpace: "nowrap",
              }}
            >
              {TAB_LABELS[t]} <span style={{ fontWeight: 400, marginLeft: 4 }}>({getCount(t)})</span>
            </button>
          ))}
        </div>

        {/* Suche */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "8px 12px",
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: 16, color: "var(--fg-muted)" }}>
              search
            </span>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Gruppe, Kontakt, E-Mail oder Ort..."
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                color: "var(--fg)",
              }}
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
                style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, lineHeight: 1 }}
              >
                <span className="material-icons-outlined" style={{ fontSize: 16, color: "var(--fg-muted)" }}>
                  close
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Zeitraum-Filter */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--fg-muted)", whiteSpace: "nowrap", marginRight: 4 }}>
            Zeitraum:
          </span>
          <label
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--fg-muted)", whiteSpace: "nowrap" }}
          >
            ab
            <input
              type="date"
              value={filterVon}
              max={filterDateRange.fromProps.max}
              onChange={(e) => filterDateRange.setFrom(e.target.value)}
              style={{
                padding: "6px 8px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: filterVon ? "var(--accent)" : "var(--bg-card)",
                color: filterVon ? "#fff" : "var(--fg)",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                colorScheme: "light dark",
              }}
            />
          </label>
          <label
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--fg-muted)", whiteSpace: "nowrap" }}
          >
            bis
            <input
              type="date"
              value={filterBis}
              min={filterDateRange.toProps.min}
              onChange={(e) => filterDateRange.setTo(e.target.value)}
              style={{
                padding: "6px 8px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: filterBis ? "var(--accent)" : "var(--bg-card)",
                color: filterBis ? "#fff" : "var(--fg)",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                colorScheme: "light dark",
              }}
            />
          </label>
          {hasActiveFilter && (
            <button
              onClick={() => {
                setFilterVon("");
                setFilterBis("");
                setPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: "transparent",
                color: "var(--fg-muted)",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              ✕ Zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Tabelle */}
      <Card padded={false}>
        {isLoading && <div style={{ padding: 24, fontSize: 13, color: "var(--fg-muted)" }}>Lade…</div>}
        {isError && (
          <div style={{ padding: 24, fontSize: 13, color: "var(--status-cancel)" }}>
            Anfragen konnten nicht geladen werden.
          </div>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <div style={{ padding: 32, fontSize: 13, color: "var(--fg-muted)", textAlign: "center" }}>
            <span className="material-icons-outlined" style={{ fontSize: 36, display: "block", marginBottom: 8 }}>
              mark_email_unread
            </span>
            Keine Anfragen in dieser Kategorie.
          </div>
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)", background: "var(--bg-subtle)" }}>
                  <th style={thStyle}>EINGEGANGEN</th>
                  <th style={thStyle}>GRUPPE / KONTAKT</th>
                  <th style={thStyle}>ZEITRAUM</th>
                  <th style={thStyle}>EINHEITEN</th>
                  <th style={thStyle}>NACHRICHT</th>
                  <th style={thStyle}>STATUS</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((inq, idx) => {
                  const expanded = expandedId === inq.id;
                  const last = idx === paginated.length - 1;
                  return (
                    <Fragment key={inq.id}>
                      <tr
                        onClick={() => setExpandedId(expanded ? null : inq.id)}
                        style={{
                          borderBottom: expanded || !last ? "1px solid var(--border)" : "none",
                          cursor: "pointer",
                          background: expanded ? "var(--bg-subtle)" : "transparent",
                        }}
                      >
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: "var(--fg-muted)" }}>
                          {formatDatum(inq.created_at)}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontWeight: 600 }}>{inq.gruppenname || "–"}</div>
                          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
                            {inq.kontakt}
                            {inq.email ? ` · ${inq.email}` : ""}
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                          {formatDatum(inq.check_in)} – {formatDatum(inq.check_out)}
                        </td>
                        <td style={{ padding: "10px 14px", color: "var(--fg-muted)", whiteSpace: "nowrap" }}>
                          {inq.einheiten_wunsch || "–"}
                        </td>
                        <td style={{ padding: "10px 14px", maxWidth: 220 }}>
                          {inq.nachricht ? (
                            <span
                              style={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "var(--fg)",
                              }}
                              title="Zeile anklicken für die ganze Nachricht"
                            >
                              {inq.nachricht}
                            </span>
                          ) : (
                            <span style={{ color: "var(--fg-muted)" }}>–</span>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <StatusPill status={inq.status} />
                        </td>
                        <td style={{ padding: "10px 14px" }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                            {(inq.status === "new" || inq.status === "verified") && (
                              <>
                                <Btn variant="primary" size="sm" onClick={() => setPromoteTarget(inq)}>
                                  Übernehmen
                                </Btn>
                                <Btn variant="secondary" size="sm" onClick={() => setRejectTarget(inq)}>
                                  Ablehnen
                                </Btn>
                              </>
                            )}
                            {inq.status === "imported" && inq.imported_booking_id != null && (
                              <Btn
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(`/bookings/${inq.imported_booking_id}`)}
                              >
                                Buchung #{inq.imported_booking_id}
                              </Btn>
                            )}
                            <Btn variant="danger" size="sm" onClick={() => setDeleteTarget(inq)}>
                              Löschen
                            </Btn>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr style={{ borderBottom: !last ? "1px solid var(--border)" : "none" }}>
                          <td colSpan={7} style={{ padding: "12px 14px", background: "var(--bg-subtle)" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px", fontSize: 12 }}>
                              <div>
                                <span style={{ fontWeight: 600, color: "var(--fg-muted)" }}>Zeiten:</span>{" "}
                                {inq.check_in_time || "15:00"} (Anreise) / {inq.check_out_time || "10:00"} (Abreise)
                              </div>
                              <div>
                                <span style={{ fontWeight: 600, color: "var(--fg-muted)" }}>Telefon:</span>{" "}
                                {inq.telefon || "–"}
                              </div>
                              <div style={{ gridColumn: "1/-1" }}>
                                <span style={{ fontWeight: 600, color: "var(--fg-muted)" }}>Adresse:</span>{" "}
                                {[inq.adresse_strasse, `${inq.adresse_plz} ${inq.adresse_ort}`.trim(), inq.adresse_land]
                                  .filter(Boolean)
                                  .join(", ") || "–"}
                              </div>
                              {inq.gruppenart && (
                                <div>
                                  <span style={{ fontWeight: 600, color: "var(--fg-muted)" }}>Gruppenart:</span>{" "}
                                  {inq.gruppenart}
                                </div>
                              )}
                              {inq.status === "rejected" && (
                                <div style={{ gridColumn: "1/-1" }}>
                                  <span style={{ fontWeight: 600, color: "var(--status-cancel)" }}>Ablehnungsgrund:</span>{" "}
                                  {inq.rejected_reason || "(kein Grund angegeben)"}
                                </div>
                              )}
                              {inq.nachricht && (
                                <div style={{ gridColumn: "1/-1" }}>
                                  <div style={{ fontWeight: 600, color: "var(--fg-muted)", marginBottom: 4 }}>
                                    Nachricht:
                                  </div>
                                  <div style={{ whiteSpace: "pre-wrap", color: "var(--fg)", fontSize: 13 }}>
                                    {inq.nachricht}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 18px",
                borderTop: "1px solid var(--border)",
                fontSize: 12,
                color: "var(--fg-muted)",
              }}
            >
              <span>{filtered.length} Einträge</span>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    background: page === 1 ? "var(--bg-subtle)" : "var(--bg-card)",
                    color: "var(--fg)",
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    fontSize: 12,
                    fontWeight: 500,
                    opacity: page === 1 ? 0.5 : 1,
                  }}
                >
                  ← Vorherige
                </button>
                <span style={{ minWidth: 120, textAlign: "center" }}>
                  Seite {page} von {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    background: page >= totalPages ? "var(--bg-subtle)" : "var(--bg-card)",
                    color: "var(--fg)",
                    cursor: page >= totalPages ? "not-allowed" : "pointer",
                    fontSize: 12,
                    fontWeight: 500,
                    opacity: page >= totalPages ? 0.5 : 1,
                  }}
                >
                  Nächste →
                </button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
